import { z } from 'zod';
import {
  AIR_ID_GROUP_SIZES,
  AIR_ID_TOTAL_LENGTH,
  AIR_ID_PADDING_BITS,
  BASE32_ALPHABET_LOWERCASE,
  BASE32_BITS_PER_CHAR,
  BASE32_DECODE_MAP,
  BASE32_MASK,
  UUID_HEX_LENGTH,
  UUID_HEX_REGEX,
  formatAirIdGroups,
  formatUuid,
  normalizeBase32Char,
  removeHyphens,
  validateAirIdFormat,
  isRawAirId,
} from './underlying.js';

export { isRawAirId, splitUuid } from './underlying.js';

//#region Lower level

/**
 * A validated UUID string (with or without hyphens).
 */
export type Uuid = string & { __uuid: true };

/**
 * Returns `true` if the given string is a valid UUID, with or without hyphens.
 */
export function isUuid<S extends string>(input: S): input is S & Uuid {
  const withoutHyphens = removeHyphens(input);
  return UUID_HEX_REGEX.test(withoutHyphens);
}

/**
 * Converts a Base 32 Crockford Air ID back to its original UUID.
 *
 * @param airId - RawAirId in format xxxxxx-xxxxxx-xxxxxxx-xxxxxxx (6-6-7-7, accepts both cases and ambiguous chars)
 * @returns Uuid in standard format (with hyphens)
 * @throws `TypeError` if Air ID format is invalid
 *
 * @example
 * ```ts
 * uuidFromAirId("06cqxr-c30hra-5bbf2zb-skw9je4")
 * // Returns: "01997ee1-8304-70a2-ad6f-17d799f13271"
 * ```
 *
 * @remarks
 * The 2 bits of zero-padding added during encoding are removed by right-shifting
 * the decoded value back to 128 bits before converting to hex.
 * Accepts both uppercase and lowercase input, as well as ambiguous characters (O, I, L).
 */
export function uuidFromAirId(airId: string | RawAirId): Uuid {
  if (!validateAirIdFormat(airId)) {
    throw new TypeError(`Invalid Air ID format: ${airId}`);
  }

  // Remove hyphens and decode from Base 32 to BigInt in one pass
  // Normalize and decode simultaneously for better performance
  const withoutHyphens = removeHyphens(airId);
  let bigInt = BigInt(0);

  for (let i = 0; i < withoutHyphens.length; i++) {
    const char = withoutHyphens[i];
    const normalized = normalizeBase32Char(char);
    const value = BASE32_DECODE_MAP[normalized.toUpperCase()];
    if (value === undefined) {
      throw new TypeError(`Invalid Base 32 character: ${char} in Air ID: ${airId}`);
    }
    bigInt = bigInt << BigInt(BASE32_BITS_PER_CHAR);
    bigInt = bigInt | BigInt(value);
  }

  // Remove padding (right shift by 2 bits to get back to 128 bits)
  const uuidBigInt = bigInt >> BigInt(AIR_ID_PADDING_BITS);

  // Convert BigInt to hex string (32 hex chars = 128 bits)
  const hexString = uuidBigInt.toString(16).padStart(UUID_HEX_LENGTH, '0');

  // Format as UUID: 8-4-4-4-12
  return formatUuid(hexString) as Uuid;
}

/**
 * A validated Base 32 Crockford Air ID in lowercase format.
 *
 * Air IDs are losslessly-compressed forms of UUIDs, encoded in Base 32 Crockford
 * and formatted into four groups: `xxxxxx-xxxxxx-xxxxxxx-xxxxxxx` (6-6-7-7, lowercase).
 * The encoding process pads the 128-bit UUID to 130 bits (26 characters × 5 bits per character)
 * by left-shifting the UUID by 2 bits. This padding is removed during decoding by right-shifting
 * back to the original 128 bits.
 *
 * This is the raw Air ID without entity type prefix. For entity-specific Air IDs, use `AirId<T>`.
 *
 * @example
 * ```ts
 * const rawId: RawAirId = '06cqxr-c30hra-5bbf2zb-skw9je4';
 * ```
 */
export type RawAirId = string & { __airId: true };

/**
 * Converts a UUID string to its Base 32 Crockford Air ID representation.
 *
 * @param uuid - A UUID string in standard format (e.g., "01997ee1-8304-70a2-ad6f-17d799f13271")
 * @returns RawAirId in lowercase format: xxxxxx-xxxxxx-xxxxxxx-xxxxxxx (6-6-7-7)
 * @throws `TypeError` if UUID format is invalid
 *
 * @example
 * ```ts
 * airIdFromUuid("01997ee1-8304-70a2-ad6f-17d799f13271")
 * // Returns: "06cqxr-c30hra-5bbf2zb-skw9je4"
 * ```
 *
 * @remarks
 * The UUID is padded from 128 bits to 130 bits (2 bits of zero-padding) before encoding
 * to fit the 26-character Base 32 format. This padding is automatically removed during decoding.
 * The output is always lowercase.
 */
export function airIdFromUuid(uuid: string | Uuid): RawAirId {
  // Remove hyphens and validate UUID format
  const uuidWithoutHyphens = removeHyphens(uuid);
  if (!UUID_HEX_REGEX.test(uuidWithoutHyphens)) {
    throw new TypeError(`Invalid UUID format: ${uuid}`);
  }

  // Convert hex string to BigInt (128 bits)
  const uuidBigInt = BigInt('0x' + uuidWithoutHyphens);

  // Convert to Base 32 Crockford (26 characters = 130 bits, but UUID is 128 bits)
  // We need to pad to 130 bits (2 bits of padding)
  const paddedBigInt = uuidBigInt << BigInt(AIR_ID_PADDING_BITS);

  // Encode to Base 32 (use array for efficient string building)
  const result: string[] = new Array(AIR_ID_TOTAL_LENGTH);
  let remaining = paddedBigInt;

  for (let i = AIR_ID_TOTAL_LENGTH - 1; i >= 0; i--) {
    const index = Number(remaining & BigInt(BASE32_MASK));
    result[i] = BASE32_ALPHABET_LOWERCASE[index];
    remaining = remaining >> BigInt(BASE32_BITS_PER_CHAR);
  }

  // Format into 6-6-7-7 groups
  return formatAirIdGroups(result.join('')) as RawAirId;
}

/**
 * Converts a UUID or RawAirId to RawAirId. Returns the input as-is if it's already a RawAirId.
 */
export function toRawAirId(id: string | RawAirId | Uuid): RawAirId {
  return isRawAirId(id) ? id : airIdFromUuid(id);
}

/**
 * Helper function that checks if an ID has a valid entity prefix and extracts the RawAirId.
 * Returns both the RawAirId and whether a prefix was present.
 * Uses AirId.parse() internally to avoid duplicating parsing logic.
 *
 * @param id - String that may have an entity prefix (e.g., 'cls:xxxxx-xxxxx-xxxxxxx-xxxxxxx') or be a RawAirId/UUID
 * @returns An object with hasPrefix (boolean) and rawId (RawAirId)
 *
 * @example
 * ```ts
 * const { hasPrefix, rawId } = parseEntityId('cls:06cqxr-c30hra-5bbf2zb-skw9je4');
 * // hasPrefix === true
 * // rawId === '06cqxr-c30hra-5bbf2zb-skw9je4'
 *
 * const { hasPrefix, rawId } = parseEntityId('06cqxr-c30hra-5bbf2zb-skw9je4');
 * // hasPrefix === false
 * // rawId === '06cqxr-c30hra-5bbf2zb-skw9je4'
 * ```
 */
export function parseEntityId(id: string | AirId<EntityType> | RawAirId | Uuid): {
  hasPrefix: boolean;
  rawId: RawAirId;
} {
  try {
    const parsed = AirId.parse(id);
    return {
      hasPrefix: parsed.entityType !== undefined,
      rawId: parsed.rawId,
    };
  } catch {
    // If parsing fails, treat as RawAirId or UUID and convert
    return {
      hasPrefix: false,
      rawId: toRawAirId(id),
    };
  }
}

/**
 * Returns the display form of an Air ID (lowercase last two groups).
 *
 * @param airId - RawAirId in format xxxxxx-xxxxxx-xxxxxxx-xxxxxxx (6-6-7-7)
 * @returns Display form: lowercase last two groups (e.g., "5bbf2z-bskw9j")
 * @throws `TypeError` if Air ID format is invalid
 *
 */
export function stubOfAirId(airId: string | RawAirId): string {
  if (!validateAirIdFormat(airId)) {
    throw new TypeError(`Invalid Air ID format: ${airId}`);
  }

  // Remove hyphens, get last 2 groups (7 + 7 = 14 characters)
  const withoutHyphens = removeHyphens(airId);
  const lastTwoGroupsLength = AIR_ID_GROUP_SIZES[2] + AIR_ID_GROUP_SIZES[3]; // 7 + 7 = 14
  const lastTwoGroups = withoutHyphens.slice(-lastTwoGroupsLength);

  // Format as two groups and lowercase (already lowercase if from airIdFromUuid, but normalize for safety)
  return `${lastTwoGroups.slice(0, AIR_ID_GROUP_SIZES[2])}-${lastTwoGroups.slice(AIR_ID_GROUP_SIZES[2], lastTwoGroupsLength)}`.toLowerCase();
}

//#endregion

//#region Zod Schemas

/**
 * Zod schema for validating UUID strings.
 * Accepts UUIDs with or without hyphens.
 */
export const uuidSchema = z
  .string()
  .refine((val) => isUuid(val), {
    message: 'Invalid UUID format',
  })
  .transform((val) => val as Uuid);

/**
 * Zod schema for validating RawAirId (Base 32 Crockford format).
 * Enforces the 6-6-7-7 grouping and valid Base 32 characters.
 */
export const airIdSchema = z
  .string()
  .refine((val) => isRawAirId(val), {
    message: 'Invalid Air ID format (expected xxxxxx-xxxxxx-xxxxxxx-xxxxxxx)',
  })
  .transform((val) => val as RawAirId);

/**
 * Zod schema that accepts either Air ID or UUID (for migration period).
 * This allows backward compatibility during the transition from UUIDs to Air IDs.
 */
export const airIdOrUuidSchema = z.union([airIdSchema, uuidSchema]);

//#endregion

//#region Entity-specific

/**
 * Entity types that can have Air IDs.
 */
export type EntityType =
  /** classes (student groups) */
  | 'cls'
  /** sessions */
  | 'ses';

/**
 * A full Air ID with entity type prefix.
 *
 * Format: `${EntityType}:${RawAirId}`
 *
 * This type represents a validated Air ID that includes the entity type prefix,
 * providing type safety to distinguish between different entity types (e.g., student groups vs sessions).
 *
 * @example
 * ```ts
 * const sessionId: SessionAirId = 'ses:06cqxr-c30hra-5bbf2zb-skw9je4';
 * const classId: StudentGroupAirId = 'cls:06cqxr-c30hra-5bbf2zb-skw9je4';
 * ```
 */
export type AirId<T extends EntityType> = `${T}:${RawAirId}`;

/**
 * Data structure returned from AirId.parse() containing the entity type and raw ID.
 */
export interface AirIdData<T extends EntityType> {
  entityType: T | undefined;
  rawId: RawAirId;
}

/**
 * Constructs an AirId from an entity type and raw Air ID.
 *
 * @param entityType - The entity type (e.g., 'ses', 'cls')
 * @param rawId - The raw Air ID
 * @returns An AirId with the format `${entityType}:${rawId}`
 *
 * @example
 * ```ts
 * const id = AirId('ses', '06cqxr-c30hra-5bbf2zb-skw9je4' as RawAirId);
 * // id === 'ses:06cqxr-c30hra-5bbf2zb-skw9je4'
 * ```
 */
export function AirId<T extends EntityType>(entityType: T, rawId: RawAirId): AirId<T> {
  if (!isRawAirId(rawId)) {
    throw new TypeError(`Invalid RawAirId: ${rawId}`);
  }
  return `${entityType}:${rawId}` as AirId<T>;
}

/**
 * Alternative constructor that takes an object.
 *
 * @param options - Object with entityType and rawId
 * @returns An AirId with the format `${entityType}:${rawId}`
 *
 * @example
 * ```ts
 * const id = AirId.from({ entityType: 'ses', rawId: '06cqxr-c30hra-5bbf2zb-skw9je4' as RawAirId });
 * ```
 */
AirId.from = function <T extends EntityType>(options: { entityType: T; rawId: RawAirId }): AirId<T> {
  return AirId(options.entityType, options.rawId);
};

/**
 * Parses an Air ID string into its component parts.
 * Normalizes empty entityType to undefined (e.g., ':06cqxr-c30hra-5bbf2zb-skw9je4' or just a raw Air ID).
 *
 * @param input - An Air ID string (e.g., 'ses:06cqxr-c30hra-5bbf2zb-skw9je4')
 * @returns An object with entityType and rawId, or throws if invalid
 * @throws `TypeError` if the input is not a valid Air ID
 *
 * @example
 * ```ts
 * const { entityType, rawId } = AirId.parse('ses:06cqxr-c30hra-5bbf2zb-skw9je4');
 * // entityType === 'ses'
 * // rawId === '06cqxr-c30hra-5bbf2zb-skw9je4'
 * ```
 */
AirId.parse = function parse(input: string): AirIdData<EntityType> {
  const colonIndex = input.indexOf(':');
  if (colonIndex === -1) {
    // No colon, treat as raw Air ID
    if (!isRawAirId(input)) {
      throw new TypeError(`Invalid Air ID format: ${input}`);
    }
    return { entityType: undefined, rawId: input as RawAirId };
  }

  const entityType = input.slice(0, colonIndex);
  const rawId = input.slice(colonIndex + 1);

  // Normalize empty entityType to undefined
  const normalizedEntityType = entityType === '' ? undefined : (entityType as EntityType | undefined);

  if (!isRawAirId(rawId)) {
    throw new TypeError(`Invalid Air ID format: ${input}`);
  }

  return { entityType: normalizedEntityType, rawId: rawId as RawAirId };
};

/**
 * Type guard that checks if a string is a valid AirId.
 * Uses Symbol.hasInstance to enable `instanceof AirId` syntax.
 *
 * @param input - The string to check
 * @returns `true` if the input is a valid AirId, `false` otherwise
 *
 * @example
 * ```ts
 * if (AirId.is('ses:06cqxr-c30hra-5bbf2zb-skw9je4')) {
 *   // TypeScript knows this is an AirId
 * }
 * ```
 */
AirId.is = function isAirId(input: string): input is AirId<EntityType> {
  try {
    const parsed = AirId.parse(input);
    // Valid AirId must have both entityType and rawId
    return parsed.entityType !== undefined && parsed.rawId !== undefined;
  } catch {
    return false;
  }
};

/**
 * Enables `instanceof AirId` syntax using Symbol.hasInstance.
 */
Object.defineProperty(AirId, Symbol.hasInstance, {
  value: function (input: unknown): input is AirId<EntityType> {
    return typeof input === 'string' && AirId.is(input);
  },
});

/**
 * Returns `true` if the given string starts with the specified prefix and the remainder is a valid RawAirId.
 */
export function isRawAirIdWithPrefix<S extends string, P extends string>(input: S, prefix: P): boolean {
  if (!input.startsWith(prefix)) {
    return false;
  }
  const rawId = input.slice(prefix.length);
  return isRawAirId(rawId);
}

/**
 * Generic type guard factory for entity-specific Air IDs.
 * Creates a type guard that checks if a string is a valid AirId for a specific entity type.
 *
 * @param prefix - Entity type prefix (e.g., 'cls:', 'ses:')
 * @returns A type guard function
 */
function createEntityAirIdGuard<T extends EntityType>(
  prefix: `${T}:`,
): <S extends string>(input: S) => input is S & AirId<T> {
  return <S extends string>(input: S): input is S & AirId<T> => {
    return isRawAirIdWithPrefix(input, prefix);
  };
}

export const isStudentGroupAirId = createEntityAirIdGuard('cls:');

export const isSessionAirId = createEntityAirIdGuard('ses:');

/**
 * Creates a concrete AirId type with its own constructor, parser, and instanceof support.
 * This enables meta-programming for subtypes like StudentGroupAirId.
 *
 * @param entityType - The entity type (e.g., 'cls', 'ses')
 * @returns An object with constructor, parse, and instanceof support for the specific entity type
 *
 * @example
 * ```ts
 * export const StudentGroupAirId = createConcreteAirIdType('cls');
 * const id = StudentGroupAirId('06cqxr-c30hra-5bbf2zb-skw9je4' as RawAirId);
 * const { entityType, rawId } = StudentGroupAirId.parse('cls:06cqxr-c30hra-5bbf2zb-skw9je4');
 * if (someId instanceof StudentGroupAirId) { ... }
 * ```
 */
export function createConcreteAirIdType<T extends EntityType>(entityType: T) {
  const prefix = `${entityType}:` as const;

  /**
   * Constructs an AirId for this specific entity type.
   *
   * @param rawId - The raw Air ID
   * @returns An AirId with the format `${entityType}:${rawId}`
   */
  function ConcreteAirId(rawId: RawAirId): AirId<T> {
    return AirId(entityType, rawId);
  }

  /**
   * Parses an Air ID string, ensuring it matches this entity type.
   *
   * @param input - An Air ID string (e.g., 'cls:06cqxr-c30hra-5bbf2zb-skw9je4')
   * @returns An object with entityType and rawId
   * @throws `TypeError` if the input is not a valid Air ID or doesn't match this entity type
   */
  ConcreteAirId.parse = function parse(input: string): AirIdData<T> {
    const parsed = AirId.parse(input);
    if (parsed.entityType !== entityType) {
      throw new TypeError(
        `Expected Air ID with entity type '${entityType}', but got '${parsed.entityType ?? 'undefined'}'`,
      );
    }
    return { entityType, rawId: parsed.rawId };
  };

  /**
   * Type guard that checks if a string is a valid AirId for this entity type.
   *
   * @param input - The string to check
   * @returns `true` if the input is a valid AirId for this entity type
   */
  ConcreteAirId.is = function isConcreteAirId(input: string): input is AirId<T> {
    return isRawAirIdWithPrefix(input, prefix);
  };

  /**
   * Enables `instanceof ConcreteAirId` syntax using Symbol.hasInstance.
   */
  Object.defineProperty(ConcreteAirId, Symbol.hasInstance, {
    value: function (input: unknown): input is AirId<T> {
      return typeof input === 'string' && ConcreteAirId.is(input);
    },
  });

  return ConcreteAirId;
}

export type StudentGroupAirId = AirId<'cls'>;

export type SessionAirId = AirId<'ses'>;

export const StudentGroupAirId = createConcreteAirIdType('cls');

export const SessionAirId = createConcreteAirIdType('ses');

/**
 * Extracts the UUID from an entity ID, RawAirId, or UUID string.
 * Handles migration period: accepts AirId with prefix, RawAirId, or UUID.
 *
 * @param id - Entity AirId, RawAirId, or UUID string
 * @param prefix - Entity type prefix (e.g., 'cls:', 'ses:')
 * @param entityName - Name of the entity for error messages (e.g., 'student group', 'session')
 * @param typeGuard - Type guard function for the entity-specific AirId
 * @returns UUID string in standard format
 * @throws `TypeError` if the ID format is invalid
 *
 * @example
 * ```ts
 * // Accepts entity-prefixed Air ID
 * uuidFromEntityId('cls:06cqxr-c30hra-5bbf2zb-skw9je4', 'cls:', 'student group', isStudentGroupAirId)
 * // Returns: "01997ee1-8304-70a2-ad6f-17d799f13271"
 *
 * // Accepts RawAirId
 * uuidFromEntityId('06cqxr-c30hra-5bbf2zb-skw9je4', 'cls:', 'student group', isStudentGroupAirId)
 * // Returns: "01997ee1-8304-70a2-ad6f-17d799f13271"
 *
 * // Accepts UUID (returns as-is)
 * uuidFromEntityId('01997ee1-8304-70a2-ad6f-17d799f13271', 'cls:', 'student group', isStudentGroupAirId)
 * // Returns: "01997ee1-8304-70a2-ad6f-17d799f13271"
 * ```
 */
export function uuidFromEntityId<T extends EntityType>(
  id: string | AirId<T> | RawAirId | Uuid,
  prefix: `${T}:`,
  entityName: string,
  typeGuard: <S extends string>(input: S) => input is S & AirId<T>,
): Uuid {
  if (typeGuard(id)) {
    // Extract RawAirId by removing prefix
    const rawId = id.slice(prefix.length) as RawAirId;
    return uuidFromAirId(rawId);
  }
  if (isRawAirId(id)) {
    return uuidFromAirId(id);
  }
  if (isUuid(id)) {
    return id;
  }
  throw new TypeError(`Invalid ${entityName} ID format: ${id}`);
}

/**
 * Factory function that creates a UUID converter for a specific entity type.
 * Handles migration period: accepts AirId with prefix, RawAirId, or UUID.
 *
 * @param prefix - Entity type prefix (e.g., 'cls:', 'ses:')
 * @param entityName - Name of the entity for error messages (e.g., 'student group', 'session')
 * @param typeGuard - Type guard function for the entity-specific AirId
 * @returns A function that converts various ID formats to UUID
 *
 * @example
 * ```ts
 * // Accepts: 'cls:xxxxxx-xxxxxx-xxxxxxx-xxxxxxx', 'xxxxxx-xxxxxx-xxxxxxx-xxxxxxx', or UUID
 * uuidFromStudentGroupId('cls:06cqxr-c30hra-5bbf2zb-skw9je4')
 * // Returns: "01997ee1-8304-70a2-ad6f-17d799f13271"
 * ```
 */
function createUuidFromEntityId<T extends EntityType>(
  prefix: `${T}:`,
  entityName: string,
  typeGuard: <S extends string>(input: S) => input is S & AirId<T>,
): (id: string | AirId<T> | RawAirId | Uuid) => Uuid {
  return (id: string | AirId<T> | RawAirId | Uuid): Uuid => {
    return uuidFromEntityId(id, prefix, entityName, typeGuard);
  };
}

export const uuidFromStudentGroupId = createUuidFromEntityId('cls:', 'student group', isStudentGroupAirId);

export const uuidFromSessionId = createUuidFromEntityId('ses:', 'session', isSessionAirId);

//#endregion
